import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ParsedCliffNote } from "@/features/trending/types/CliffNote";
import { getMostRecentDate } from "@/lib/utils";
import { CompleteCliffNote } from "@/features/trending/types/CliffNote";
import { Article } from "@/features/search/types/search";

interface CliffNoteProps {
  cliffNote: CompleteCliffNote;
  cliffNoteArticles: Article[];
}

export default function CliffNote({
  cliffNote,
  cliffNoteArticles,
}: CliffNoteProps) {
  if (!cliffNote?.openAICliffNote?.content) {
    console.log("No cliff note content");
    return null;
  }

  const parsedContent: ParsedCliffNote = JSON.parse(
    cliffNote.openAICliffNote.content
  );

  const {
    main_point = "",
    why_care = "",
    key_facts = [],
    whats_next = [],
  } = parsedContent;

  return (
    <Card className="p-6 mt-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-black rounded-xl shadow-none">
      <div className="flex justify-between items-start mb-4">
        <div className="flex flex-col gap-1 w-full">
          <div className="flex items-center gap-4 justify-between">
            <h3 className="text-xl font-semibold tracking-tight text-black">
              {cliffNote.searchQuery}
            </h3>
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs px-2 py-1 h-auto border-gray-300 bg-white hover:bg-gray-100"
                  >
                    Get timeline view
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs leading-snug">
                  Coming soon. You'll be able to track how this topic developed
                  over time.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {cliffNoteArticles && getMostRecentDate(cliffNoteArticles) && (
            <span className="text-xs text-gray-500">
              Latest source: {getMostRecentDate(cliffNoteArticles)}
            </span>
          )}
        </div>
      </div>

      <div className="space-y-5 text-sm leading-relaxed text-gray-700">
        <div>
          <h4 className="font-semibold text-black mb-1">Key Takeaway</h4>
          <p>{main_point}</p>
        </div>

        <div>
          <h4 className="font-semibold text-black mb-1">Why It Matters</h4>
          <p>{why_care}</p>
        </div>

        {key_facts.length > 0 && (
          <div>
            <h4 className="font-semibold text-black mb-1">Key Facts</h4>
            <ul className="list-disc pl-5 space-y-1">
              {key_facts.map((factItem, index) => (
                <li key={index}>
                  <a
                    href={factItem.source}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {factItem.fact}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {whats_next.length > 0 && (
          <div>
            <h4 className="font-semibold text-black mb-1">What's Next</h4>
            <ul className="list-disc pl-5 space-y-1">
              {whats_next.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {cliffNoteArticles.length > 0 && (
          <div className="pt-4 border-t border-black/20 mt-6">
            <Accordion type="single" collapsible>
              <AccordionItem value="articles">
                <AccordionTrigger className="text-sm font-semibold">
                  Analyzed Articles
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-2 text-xs text-gray-600 pt-2">
                    {cliffNoteArticles
                      .slice(0, key_facts.length + 1)
                      .map((article, index) => (
                        <div key={index} className="flex gap-2">
                          <span className="font-medium text-gray-500">
                            [{index + 1}]
                          </span>
                          <a
                            href={article.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                          >
                            {article.title} – {article.author}
                            {article.publish_date && (
                              <span className="ml-1 text-gray-400">
                                (
                                {new Date(
                                  article.publish_date
                                ).toLocaleDateString()}
                                )
                              </span>
                            )}
                          </a>
                        </div>
                      ))}

                    {cliffNoteArticles.length > key_facts.length && (
                      <>
                        <div className="py-2 text-sm font-medium text-gray-600">
                          Additional Related Articles
                        </div>
                        {cliffNoteArticles
                          .slice(key_facts.length)
                          .map((article, index) => (
                            <div key={index} className="flex gap-2 opacity-70">
                              <span className="font-medium text-gray-400">
                                [{index + key_facts.length + 2}]
                              </span>
                              <a
                                href={article.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:underline"
                              >
                                {article.title} – {article.author}
                                {article.publish_date && (
                                  <span className="ml-1 text-gray-400">
                                    (
                                    {new Date(
                                      article.publish_date
                                    ).toLocaleDateString()}
                                    )
                                  </span>
                                )}
                              </a>
                            </div>
                          ))}
                      </>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        )}
      </div>
    </Card>
  );
}
